import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import RobotFace from "@/components/RobotFace";
import * as L from "lucide-react";

export function EmailsScreen({ user, go }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyFor, setReplyFor] = useState(null); // full email object
  const [scanning, setScanning] = useState(false);

  const findPromises = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/promises/scan");
      toast.success(
        data.suggested
          ? `Found ${data.suggested} possible promise${data.suggested === 1 ? "" : "s"} — confirm them on Today`
          : "Nothing that looks like a promise in your recent mail",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read your mail just now");
    } finally { setScanning(false); }
  };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/gmail/inbox");
      setItems(data.items || []);
      if (data.connected === false) setError(data.detail || "Google isn't connected.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      setError(detail);
    } finally { setLoading(false); }
  };
  useEffect(() => { load();   }, []);

  const notConnected = error && error.toLowerCase().includes("google isn't connected");

  return (
    <div className="p-5 space-y-4" data-testid="emails-screen">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-2xl font-display">Focus inbox</div>
          <p className="text-sm text-[var(--rammy-text-sec)] mt-1">
            The three unread emails that matter, plus a warm draft you approve before sending.
          </p>
        </div>
        <button onClick={load} className="text-xs uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold" data-testid="emails-refresh">Refresh</button>
      </div>

      {!notConnected && (
        <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-4 flex items-start justify-between gap-4 flex-wrap" data-testid="emails-promise-scan">
          <div className="min-w-0">
            <div className="text-sm font-medium">Promises hiding in your mail</div>
            <p className="text-xs text-[var(--rammy-text-sec)] mt-1 leading-relaxed max-w-md">
              Rammy reads your recent threads for things you said you&rsquo;d do — and things people said they&rsquo;d do for you. You confirm each one before it counts. We don&rsquo;t store your emails.
            </p>
          </div>
          <Button onClick={findPromises} disabled={scanning}
            className="btn-pill h-9 px-4 bg-[var(--rm-ink-900)] text-white hover:opacity-90"
            data-testid="emails-find-promises">
            {scanning ? "Reading…" : "Find promises"}
          </Button>
        </div>
      )}

      {loading && <div className="text-sm text-[var(--rammy-text-muted)] py-8 text-center">Reading your inbox…</div>}

      {!loading && notConnected && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="emails-need-google">
          <div className="text-sm font-medium">Connect Google first</div>
          <p className="text-xs text-[var(--rammy-text-sec)] mt-1 leading-relaxed">
            Gmail rides on the same Google connection Rammy uses for Calendar. Connect once and both work.
          </p>
          <Button onClick={()=>go("calendar")} className="btn-pill h-9 px-4 mt-3 bg-[var(--rm-ink-900)] text-white hover:opacity-90" data-testid="emails-connect-google">
            Connect Google
          </Button>
        </div>
      )}

      {!loading && !notConnected && error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="emails-error">
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-8 text-center" data-testid="emails-empty">
          <div className="mx-auto w-14 h-14 rounded-full bg-[var(--rammy-surface-alt)] flex items-center justify-center mb-3">
            <L.Mail size={20} className="text-[var(--rammy-text-muted)]"/>
          </div>
          <div className="font-medium">Inbox zero.</div>
          <p className="text-xs text-[var(--rammy-text-muted)] mt-1 max-w-xs mx-auto leading-relaxed">
            Beautiful — nothing unread that Rammy needs to worry you about.
          </p>
        </div>
      )}

      <div className="space-y-2">
        {items.map(e => (
          <div key={e.id} className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-4" data-testid={`email-${e.id}`}>
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-xl bg-[var(--rammy-surface-alt)] flex items-center justify-center shrink-0">
                <L.Mail size={14} className="text-[var(--rammy-cyan-dark)]"/>
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 justify-between">
                  <div className="text-sm font-medium truncate">{e.from?.name || e.from?.email || "(unknown)"}</div>
                  <div className="text-[10px] font-mono text-[var(--rammy-text-muted)] shrink-0">
                    {e.date ? new Date(e.date).toLocaleDateString([], {month:"short", day:"numeric"}) : ""}
                  </div>
                </div>
                <div className="text-sm mt-0.5 truncate">{e.subject}</div>
                <div className="text-xs text-[var(--rammy-text-muted)] mt-1 line-clamp-2 leading-relaxed">{e.snippet}</div>
                <div className="mt-3">
                  <Button
                    onClick={() => setReplyFor(e)}
                    className="btn-pill h-8 px-3 bg-[var(--rm-ink-900)] text-white hover:opacity-90 text-xs"
                    data-testid={`email-draft-${e.id}`}
                  >
                    <L.Sparkles size={12} className="mr-1"/> Draft warm reply
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {replyFor && <DraftReplyModal email={replyFor} onClose={()=>setReplyFor(null)}/>}
    </div>
  );
}

function DraftReplyModal({ email, onClose }) {
  const [intent, setIntent] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [gmailUrl, setGmailUrl] = useState("");
  const [confirmingSend, setConfirmingSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentMsgId, setSentMsgId] = useState(null);

  const submit = async () => {
    if (!intent.trim() || busy) return;
    setBusy(true); setPreview(""); setDraftId(null);
    try {
      const { data } = await api.post("/gmail/draft-reply", {
        message_id: email.id,
        thread_id: email.thread_id,
        to_email: email.from?.email || "",
        subject: email.subject || "",
        in_reply_to_msgid: email.message_id || null,
        intent: intent.trim(),
      });
      setPreview(data.draft_body || "");
      setDraftId(data.draft_id || null);
      setGmailUrl(data.gmail_open_url || "https://mail.google.com/mail/u/0/#drafts");
      toast.success("Draft saved in your Gmail Drafts");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the draft");
    } finally { setBusy(false); }
  };

  const sendNow = async () => {
    // Two-tap safety: first tap arms, second tap sends. Auto-disarm after 4s.
    if (!confirmingSend) {
      setConfirmingSend(true);
      setTimeout(() => setConfirmingSend(false), 4000);
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/gmail/send-reply", {
        message_id: email.id,
        thread_id: email.thread_id,
        to_email: email.from?.email || "",
        subject: email.subject || "",
        in_reply_to_msgid: email.message_id || null,
        body: preview,
      });
      setSentMsgId(data.message_id);
      setGmailUrl(data.gmail_open_url || "https://mail.google.com/mail/u/0/#sent");
      toast.success(`Reply sent to ${email.from?.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gmail refused to send");
      setConfirmingSend(false);
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="draft-modal">
      <div className="w-full max-w-lg rounded-3xl bg-[var(--rm-surface)] p-6 fade-up" onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${sentMsgId ? "bg-emerald-100 text-emerald-700" : "bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)]"}`}>
            {sentMsgId ? <L.Check size={18}/> : <L.Sparkles size={18}/>}
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-display text-lg leading-tight truncate">
              {sentMsgId ? "Sent" : "Draft a reply"}
            </div>
            <div className="text-xs text-[var(--rammy-text-muted)] truncate">Re: {email.subject}</div>
            <div className="text-[11px] font-mono text-[var(--rammy-text-muted)] mt-0.5 truncate">to {email.from?.email}</div>
          </div>
        </div>

        {/* SENT STATE */}
        {sentMsgId && (
          <>
            <div className="mt-5 rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900 leading-relaxed" data-testid="sent-success">
              Your reply is on its way. It&rsquo;ll appear in your Gmail Sent folder.
            </div>
            <div className="mt-4 flex justify-end gap-2 flex-wrap">
              <a href={gmailUrl} target="_blank" rel="noopener noreferrer" data-testid="sent-open-gmail">
                <Button variant="ghost" className="btn-pill border border-[var(--rammy-border)]">
                  Open Gmail Sent <L.ExternalLink size={13} className="ml-1.5"/>
                </Button>
              </a>
              <Button onClick={onClose} className="btn-pill btn-primary" data-testid="sent-close">Done</Button>
            </div>
          </>
        )}

        {/* COMPOSE STATE */}
        {!preview && !sentMsgId && (
          <>
            <label className="mt-5 block text-xs uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold">What&rsquo;s your intent?</label>
            <textarea
              value={intent}
              onChange={(e)=>setIntent(e.target.value)}
              placeholder="e.g. Accept for Tuesday 3pm · say I'm out until Monday · ask them to send the deck first"
              rows={3}
              autoFocus
              disabled={busy}
              data-testid="draft-intent-input"
              className="mt-1 w-full rounded-2xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] p-3 text-sm outline-none focus:border-[var(--rammy-cyan)] resize-none"
            />
            <div className="mt-2 text-[11px] text-[var(--rammy-text-muted)] leading-relaxed">
              Rammy will draft the reply. You&rsquo;ll see the whole message before anything is sent.
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button onClick={onClose} variant="ghost" className="btn-pill border border-[var(--rammy-border)]" data-testid="draft-cancel">Cancel</Button>
              <Button onClick={submit} disabled={busy || !intent.trim()} className="btn-pill btn-primary" data-testid="draft-submit">
                {busy ? <><L.Loader2 size={14} className="animate-spin mr-1.5"/> Composing…</> : "Draft it"}
              </Button>
            </div>
          </>
        )}

        {/* REVIEW STATE — editable preview + send/open-gmail */}
        {preview && !sentMsgId && (
          <>
            <div className="mt-5 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold">Rammy drafted · edit before sending</div>
              <button onClick={()=>{ setPreview(""); setDraftId(null); setConfirmingSend(false); }} className="text-[10px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold" data-testid="draft-retry">Rewrite</button>
            </div>
            <textarea
              value={preview}
              onChange={(e)=>setPreview(e.target.value)}
              rows={10}
              disabled={sending}
              data-testid="draft-preview"
              className="mt-2 w-full rounded-2xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] p-4 text-sm leading-relaxed outline-none focus:border-[var(--rammy-cyan)] resize-none font-sans"
              style={{ minHeight: 200 }}
            />
            <div className="mt-2 text-[11px] text-[var(--rammy-text-muted)] leading-relaxed">
              Draft is saved in Gmail. You can tweak the words here, then send from Rammy — or open Gmail to send there.
            </div>
            <div className="mt-4 flex justify-end gap-2 flex-wrap">
              <a href={gmailUrl} target="_blank" rel="noopener noreferrer" data-testid="draft-open-gmail">
                <Button variant="ghost" className="btn-pill border border-[var(--rammy-border)]">
                  Open in Gmail <L.ExternalLink size={13} className="ml-1.5"/>
                </Button>
              </a>
              <Button
                onClick={sendNow}
                disabled={sending || !preview.trim()}
                data-testid="draft-send"
                data-confirming={confirmingSend ? "true" : "false"}
                className={`btn-pill ${confirmingSend
                  ? "bg-emerald-500 text-white hover:bg-emerald-600"
                  : "btn-primary"}`}
              >
                {sending ? <><L.Loader2 size={14} className="animate-spin mr-1.5"/> Sending…</>
                  : confirmingSend ? <><L.Check size={14} className="mr-1.5"/> Tap again to confirm</>
                  : <><L.Send size={14} className="mr-1.5"/> Send from Rammy</>}
              </Button>
            </div>
            {draftId && <div className="mt-2 text-[10px] font-mono text-[var(--rammy-text-muted)]">draft id: {draftId}</div>}
          </>
        )}
      </div>
    </div>
  );
}
